import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import React, { type PropsWithChildren } from 'react';
import styled from '@emotion/styled';
import useMediaQuery from '@mui/material/useMediaQuery';
import Image, { type StaticImageData } from 'next/image';

import { PrimaryButton } from '@/components/button';
import { H3, PStyles } from '@/components/type';
import { BREAKPOINTS, SPACE } from '@/constants';
import { useGutterSpacing } from '@/hooks/use-gutter-spacing';
import { ContentBlockWrapper } from '@/components/content-block-wrapper';
import { shouldForwardProp } from '@/utils/should-forward-prop';
import type { HsiycContentMediaBlock, SanityFileAsset } from '@tac/sanity-client/types';
import type { Dereference } from '@tac/sanity-client/utils';
import { urlFor } from '@/utils/sanity-image';

const Video = ({
  altText,
  autoPlay = false,
  captions,
  mimeType,
  poster,
  url,
}: Partial<SanityFileAsset> & { autoPlay?: boolean; captions?: string; poster?: string }) =>
  url ? (
    <div className="aspect-video w-full overflow-hidden bg-black">
      <video
        aria-label={altText}
        autoPlay={autoPlay}
        className="h-full w-full object-contain block align-middle"
        controls
        crossOrigin="anonymous"
        loop
        muted
        playsInline
        poster={poster}
        preload={autoPlay ? 'auto' : 'none'}
      >
        <track label="English" kind="captions" srcLang="en" src={captions} default />
        <source src={url} type={mimeType} />
      </video>
    </div>
  ) : null;

const StyledH3 = styled(H3)`
  margin-bottom: ${SPACE.X1};
`;

const StyledRichText = styled.div`
  ${PStyles};
`;

const StyledPrimaryButton = styled(PrimaryButton)`
  &.MuiButton-root {
    margin-top: ${SPACE.X2};
  }
`;

const StyledBox = styled(Box)`
  width: 100%;
  @media (min-width: ${BREAKPOINTS.LG}px) {
    width: unset;
  }
`;

const StyledImage = styled(Image)`
  width: 100%;
  height: auto;
`;

// @ts-expect-error [🤖 PLEASE FIX 🤖]: Property 'isReverse' does not exist on type 'GridOwnProps & CommonProps & Omit<Omit<DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>, "ref"> & { ...; }, "xl" | ... 120 more ... | "zeroMinWidth"> & { ...; } & { ...; }'.
const StyledGrid = styled(Grid, { shouldForwardProp })(({ isReverse, useBackgroundImage }) => {
  const mediaQueryName = `@media (min-width: ${BREAKPOINTS.LG}px)`;
  return {
    '&.MuiGrid-root': {
      justifyContent: isReverse ? 'flex-end' : 'flex-start',
      '& > .MuiGrid-item': {
        paddingTop: 0,
        paddingBottom: 0,
      },
      '& .image': {
        order: isReverse && useBackgroundImage ? 2 : 1,
      },
      '& .content': {
        order: isReverse && useBackgroundImage ? 1 : 2,
      },
      [mediaQueryName]: {
        '& .image': {
          order: isReverse ? 2 : 1,
        },
        '& .content': {
          order: isReverse ? 1 : 2,
        },
      },
    },
  };
});

type ContentMediaBlockProps = Partial<Dereference<HsiycContentMediaBlock>> & {
  imageOverride?: StaticImageData;
  imageOverrideAlt?: string;
};

const ContentMediaBlock = ({
  useBackgroundImage = false,
  children,
  media,
  imageOverride,
  imageOverrideAlt,
  heading,
  body,
  ctaButton,
  imageAlignment = 'left',
}: PropsWithChildren<ContentMediaBlockProps>) => {
  const lgUp = useMediaQuery(`(min-width: ${BREAKPOINTS.LG}px)`);
  const isReverse = imageAlignment == 'right';
  const imageUrl = urlFor(media?.image)?.url?.();
  const videoPosterUrl = urlFor(media?.videoPoster?.asset)?.url?.();

  return (
    // @ts-expect-error [🤖 PLEASE FIX 🤖]: Property 'url' does not exist on type 'string | StaticImageData | undefined'.
    <ContentBlockWrapper url={media?.type === 'image' ? imageUrl : null} useBackgroundImage={useBackgroundImage}>
      <Container maxWidth="xl">
        <Box pt={lgUp ? SPACE.X3 : SPACE.X2} pb={lgUp ? SPACE.X3 : '48px'}>
          <StyledGrid
            container
            spacing={useGutterSpacing()}
            // @ts-expect-error [🤖 PLEASE FIX 🤖]: Type '{ children: Element[]; container: true; spacing: number; isReverse: boolean; useBackgroundImage: boolean; }' is not assignable to type 'IntrinsicAttributes & GridOwnProps & CommonProps & Omit<Omit<DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>, "ref"> & { ...; }, "xl" | ... 120 more ... | "zeroMinWidth"> & { ...; }'.
            isReverse={isReverse}
            useBackgroundImage={useBackgroundImage}
          >
            <Grid item xs={useBackgroundImage ? 6 : 12} lg={6} className="image">
              {!useBackgroundImage && (
                <Box mb={lgUp ? 0 : SPACE.X2}>
                  {imageOverride && <StyledImage {...imageOverride} alt={imageOverrideAlt ?? ''} />}
                  {media?.type === 'image' ? (
                    <StyledImage
                      src={imageUrl ?? ''}
                      alt={media?.image?.asset?.altText ?? ''}
                      width={600}
                      height={450}
                      loading="lazy"
                    />
                  ) : (
                    media?.type == 'video' && (
                      <Video
                        {...media?.video?.asset}
                        autoPlay
                        poster={videoPosterUrl}
                        captions={media.videoCaptions?.asset?.url}
                      />
                    )
                  )}
                </Box>
              )}
            </Grid>
            <Grid
              item
              container
              direction="column"
              justifyContent="center"
              alignItems="center"
              xs={useBackgroundImage ? 6 : 12}
              lg={5}
              className="content"
            >
              <StyledBox
                justifyContent="flex-start"
                ml={!isReverse && lgUp ? SPACE.X2 : 0}
                mr={isReverse && lgUp ? SPACE.X2 : 0}
              >
                {heading && <StyledH3 as="h2">{heading}</StyledH3>}
                {body && <StyledRichText>{body}</StyledRichText>}
                {children}
              </StyledBox>
              <StyledBox ml={!isReverse && lgUp ? SPACE.X2 : 0} mr={isReverse && lgUp ? SPACE.X2 : 'auto'}>
                {ctaButton && ctaButton.label && (
                  <StyledPrimaryButton
                    eventText={ctaButton.label}
                    disabled={ctaButton.isEnabled === false}
                    fullWidth={!lgUp}
                    href={ctaButton.link?.external ?? `${ctaButton.link?.internal}`}
                  >
                    {ctaButton.label}
                  </StyledPrimaryButton>
                )}
              </StyledBox>
            </Grid>
          </StyledGrid>
        </Box>
      </Container>
    </ContentBlockWrapper>
  );
};

export default ContentMediaBlock;
